import { useEffect, useRef } from "react";

// GitHub-style contribution heatmap. Expects a map { 'YYYY-MM-DD': number }.
export default function Heatmap({ data = {}, weeks = 26, cellSize = 18, title = "Consistency", subtitle, testId }) {
  const scrollRef = useRef(null);
  const today = new Date();
  // Align to end-of-week (Saturday). Show `weeks` columns of 7 days.
  const cells = [];
  const totalDays = weeks * 7;
  // Start from the earliest day
  const start = new Date(today);
  start.setDate(start.getDate() - (totalDays - 1));
  // Group into columns (weeks), starting on Sunday
  const cols = [];
  let cur = new Date(start);
  // Back up to previous Sunday
  cur.setDate(cur.getDate() - cur.getDay());
  while (cur <= today) {
    const col = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(cur);
      day.setDate(day.getDate() + d);
      const iso = day.toISOString().slice(0, 10);
      const inRange = day >= start && day <= today;
      col.push({ iso, day, inRange, count: inRange ? (data[iso] || 0) : null });
    }
    cols.push(col);
    cur.setDate(cur.getDate() + 7);
  }

  const total = Object.values(data).reduce((a, b) => a + (Number(b) || 0), 0);
  const active = Object.values(data).filter((v) => (Number(v) || 0) > 0).length;
  const max = Math.max(0, ...Object.values(data).map((v) => Number(v) || 0));

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [weeks, cellSize]);

  const shade = (n) => {
    if (n == null) return "transparent";
    if (n === 0) return "rgba(255,255,255,0.04)";
    const pct = Math.min(1, n / (max || 1));
    if (pct <= 0.25) return "var(--hm-heatmap-1)";
    if (pct <= 0.50) return "var(--hm-heatmap-2)";
    if (pct <= 0.75) return "var(--hm-heatmap-3)";
    return "var(--hm-heatmap-4)";
  };

  const dayLabels = ["Mon", "Wed", "Fri"]; // sparse
  const monthLabels = [];
  let lastMonth = -1;
  cols.forEach((col, i) => {
    const m = col[0].day.getMonth();
    if (m !== lastMonth) {
      monthLabels.push({ i, label: col[0].day.toLocaleDateString(undefined, { month: "short" }) });
      lastMonth = m;
    }
  });

  const gap = 4;
  const labelCol = 26;

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">{title}</div>
          {subtitle && <div className="text-xs text-[#A1A1AA] font-light mt-1">{subtitle}</div>}
        </div>
        <div className="text-xs text-[#A1A1AA]">
          <span className="text-[#F5F5F0] font-serif-editorial text-lg mr-2">{total}</span> completions ·{" "}
          <span className="text-[#F5F5F0] font-serif-editorial text-lg mr-2">{active}</span> active days
        </div>
      </div>
      <div ref={scrollRef} className="overflow-x-auto pb-1">
        <div className="inline-block">
          {/* Month labels */}
          <div className="grid mb-2" style={{ gridTemplateColumns: `${labelCol}px repeat(${cols.length}, ${cellSize}px)`, gap: `${gap}px` }}>
            <div />
            {cols.map((_, i) => {
              const m = monthLabels.find((x) => x.i === i);
              return <div key={i} className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">{m ? m.label : ""}</div>;
            })}
          </div>
          {/* Grid */}
          <div className="grid" style={{ gridTemplateColumns: `${labelCol}px repeat(${cols.length}, ${cellSize}px)`, gap: `${gap}px` }}>
            {(() => {
              const cells = [];
              for (let row = 0; row < 7; row++) {
                cells.push(
                  <div key={`lbl-${row}`} className="text-[10px] text-[#A1A1AA] leading-none pr-1 flex items-center" style={{ height: `${cellSize}px` }}>
                    {row === 1 ? "M" : row === 3 ? "W" : row === 5 ? "F" : ""}
                  </div>
                );
                for (let ci = 0; ci < cols.length; ci++) {
                  const c = cols[ci][row];
                  cells.push(
                    <div
                      key={`cell-${row}-${ci}`}
                      title={c.inRange ? `${c.iso} · ${c.count} completion${c.count === 1 ? "" : "s"}` : ""}
                      className="rounded-[3px]"
                      style={{ width: `${cellSize}px`, height: `${cellSize}px`, backgroundColor: shade(c.count) }}
                    />
                  );
                }
              }
              return cells;
            })()}
          </div>
          {/* Legend */}
          <div className="flex items-center justify-end gap-1.5 mt-4 text-[10px] text-[#A1A1AA]">
            <span>Less</span>
            {[0, 0.25, 0.5, 0.75, 1].map((p) => (
              <div key={p} className="rounded-[3px]" style={{ width: `${cellSize - 4}px`, height: `${cellSize - 4}px`, backgroundColor: p === 0 ? "rgba(255,255,255,0.04)" : shade(Math.max(1, Math.round(p * (max || 1)))) }} />
            ))}
            <span>More</span>
          </div>
        </div>
      </div>
    </div>
  );
}
